import AudioCaptureTrigger from "@agrigate/shared-web-ui/lib/AudioCaptureTrigger";
import { InputGroup } from "@agrigate/shared-web-ui/lib/components/ui/input-group";
import { uploadImageThroughSignedUrl } from "@agrigate/shared-web-ui/lib/utils/upload";
import {
    Box,
    Button,
    CheckboxCard,
    CheckboxGroup,
    FileUpload,
    HStack,
    Image,
    Input,
    Link,
    NumberInput,
    RadioCard,
    RadioCardRoot,
    SimpleGrid,
    Text,
    VStack,
} from "@chakra-ui/react";
import { type ReactElement, useContext, useEffect, useState } from "react";
import DeleteIcon from "./assets/icons/delete-icon.svg?react";
import UploadIcon from "./assets/icons/upload-icon.svg?react";
import type { SurveyQuestion } from "./SurveyBuilderForm";
import { SurveyConfigContext } from "./SurveySubmissionForm";
import { getRPC } from "./utils/rpc";

interface ImageUrlType {
    keyPath: string;
    url: string;
}

const SurveyQuestionGenerator = (p: {
    surveyQuestion: SurveyQuestion;
    onChange: (value: string | string[]) => void;
    value?: string | string[];
}) => {
    const { surveyQuestion, onChange, value } = p;
    const { question, questionType, bannerImageKeyPath } = surveyQuestion;

    return (
        <VStack w={"inherit"}>
            {bannerImageKeyPath && (
                <Box
                    w="100%"
                    h="200px"
                    borderRadius="lg"
                    overflow="hidden"
                    boxShadow="sm"
                >
                    <Image
                        src={`${ASSETS_BUCKET_BASE_URL}/${bannerImageKeyPath}`}
                        alt="Question banner"
                        w="100%"
                        h="100%"
                        objectFit="cover"
                    />
                </Box>
            )}
            <Text
                my={25}
                fontFamily={"Poppins"}
                fontWeight={600}
                fontSize={18}
                alignSelf={"flex-start"}
            >
                {question}
            </Text>
            {questionType === "FREE_TEXT" && (
                <FreeTextInput
                    surveyQuestion={surveyQuestion}
                    onChange={onChange}
                    value={value as string}
                />
            )}
            {questionType === "RADIO_BUTTON" && (
                <RadioInput
                    surveyQuestion={surveyQuestion}
                    onChange={onChange}
                    value={Array.isArray(value) ? value[0] : (value as string)}
                />
            )}
            {questionType === "CHECKBOX" && (
                <CheckboxInput
                    surveyQuestion={surveyQuestion}
                    onChange={onChange}
                    value={value as string[]}
                />
            )}
            {questionType === "FILE_UPLOAD" && (
                <FileUploadInput
                    surveyQuestion={surveyQuestion}
                    onChange={onChange}
                    value={value as string[]}
                />
            )}
        </VStack>
    );
};

export default SurveyQuestionGenerator;

const FreeTextInput = (p: {
    surveyQuestion: SurveyQuestion;
    onChange: (value: string) => void;
    value?: string;
}): ReactElement => {
    if (p.surveyQuestion.constraint === "NUMERIC")
        return (
            <NumberInput.Root
                w={"100%"}
                value={p.value}
                onValueChange={val => p.onChange(val.value)}
            >
                <NumberInput.Input />
            </NumberInput.Root>
        );
    else
        return (
            <InputGroup
                w={"100%"}
                endElement={
                    <AudioCaptureTrigger
                        onSuccess={text => {
                            p.onChange(text);
                        }}
                    />
                }
            >
                <Input
                    bgColor={"#F2F4F6"}
                    value={p.value}
                    onChange={e => p.onChange(e.target.value)}
                />
            </InputGroup>
        );
};

const RadioInput = (p: {
    surveyQuestion: SurveyQuestion;
    onChange: (value: string) => void;
    value?: string;
}): ReactElement => {
    const { onChange, surveyQuestion, value } = p;
    const { question, options } = surveyQuestion;

    return (
        <RadioCardRoot
            onValueChange={details => {
                if (details.value) {
                    p.onChange(details.value);
                }
            }}
            value={value}
            w={"100%"}
            variant={"surface"}
            colorPalette={"green"}
            borderWidth={0}
        >
            <VStack width={"100%"}>
                {options?.map(option => (
                    <VStack key={option.id} align="flex-start" w="100%">
                        <RadioCard.Item
                            value={option.id}
                            w={"100%"}
                            borderWidth={0}
                            backgroundColor={"#F2F4F6"}
                        >
                            <RadioCard.ItemHiddenInput />
                            <RadioCard.ItemControl alignItems={"center"}>
                                <RadioCard.ItemIndicator
                                    justifySelf={"center"}
                                    verticalAlign={"center"}
                                />
                                <RadioCard.ItemContent>
                                    <HStack>
                                        {option.imageKeyPath && (
                                            <Image
                                                src={`${ASSETS_BUCKET_BASE_URL}/${option.imageKeyPath}`}
                                                alt={`Option image: ${option.value}`}
                                                width="160px"
                                                height="90px"
                                                borderRadius="md"
                                                objectFit="fill"
                                            />
                                        )}
                                        <RadioCard.ItemText>
                                            {option.value}
                                        </RadioCard.ItemText>
                                    </HStack>
                                </RadioCard.ItemContent>
                            </RadioCard.ItemControl>
                        </RadioCard.Item>
                    </VStack>
                ))}
            </VStack>
        </RadioCardRoot>
    );
};

const CheckboxInput = (p: {
    surveyQuestion: SurveyQuestion;
    onChange: (value: string[]) => void;
    value?: string[];
}): ReactElement => {
    const { question, options } = p.surveyQuestion;
    return (
        <CheckboxGroup
            onValueChange={p.onChange}
            value={p.value || []}
            w={"100%"}
        >
            <VStack>
                {options?.map((option, _idx) => (
                    <VStack key={option.id} align="flex-start" w="100%">
                        <CheckboxCard.Root
                            value={option.id}
                            w={"100%"}
                            variant={"surface"}
                            colorPalette={"green"}
                            border={"none"}
                            bgColor={"#F2F4F6"}
                        >
                            <CheckboxCard.HiddenInput />
                            <CheckboxCard.Control alignItems={"center"}>
                                <CheckboxCard.Indicator
                                    justifySelf={"center"}
                                    verticalAlign={"center"}
                                />
                                <CheckboxCard.Content>
                                    <HStack>
                                        {option.imageKeyPath && (
                                            <Image
                                                src={`${ASSETS_BUCKET_BASE_URL}/${option.imageKeyPath}`}
                                                alt={`Option image: ${option.value}`}
                                                width="160px"
                                                height="90px"
                                                borderRadius="md"
                                                objectFit="fill"
                                            />
                                        )}
                                        <CheckboxCard.Label>
                                            {option.value}
                                        </CheckboxCard.Label>
                                    </HStack>
                                </CheckboxCard.Content>
                            </CheckboxCard.Control>
                        </CheckboxCard.Root>
                    </VStack>
                ))}
            </VStack>
        </CheckboxGroup>
    );
};

type FileUploadInputProps = {
    surveyQuestion: SurveyQuestion;
    onChange: (value: string[]) => void;
    value?: string[];
};

const FileUploadInput = (p: FileUploadInputProps): ReactElement => {
    const { onChange, surveyQuestion, value = [] } = p;
    const { supportedUploadFormats } = surveyQuestion;

    const [displayFiles, setDisplayFiles] = useState<ImageUrlType[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const { surveyId } = useContext(SurveyConfigContext);

    const getAcceptedMimeTypes = () => {
        return supportedUploadFormats
            ?.map(format => {
                switch (format.toLowerCase()) {
                    case "jpg":
                    case "jpeg":
                        return "image/jpeg";
                    case "png":
                        return "image/png";
                    case "pdf":
                        return "application/pdf";
                    default:
                        return "";
                }
            })
            .filter(Boolean)
            .join(",");
    };

    const valueDependency = value ? value.join(",") : "";

    useEffect(() => {
        const fetchAndSetDisplayUrls = async () => {
            if (!value || value.length === 0) {
                setDisplayFiles([]);
                setIsLoadingFiles(false);
                return;
            }

            setIsLoadingFiles(true);
            try {
                const { images: allQuestionImages } =
                    await getRPC().surveys.getImageDownloadUrls({
                        surveyId,
                        questionId: surveyQuestion.id,
                    });

                const serverUrlMap = new Map(
                    allQuestionImages.map(img => [img.keyPath, img.url]),
                );

                setDisplayFiles(currentDisplayFiles => {
                    return value
                        .map(keyPath => {
                            const serverUrl = serverUrlMap.get(keyPath);
                            const existingFile = currentDisplayFiles.find(
                                f => f.keyPath === keyPath,
                            );
                            if (serverUrl) {
                                return { keyPath, url: serverUrl };
                            }
                            if (existingFile) {
                                return existingFile;
                            }
                            return { keyPath, url: "" };
                        })
                        .filter(file => file !== null);
                });
            } catch (error) {
                console.error("Failed to load display files:", error);
                setDisplayFiles([]);
            } finally {
                setIsLoadingFiles(false);
            }
        };

        fetchAndSetDisplayUrls();
    }, [valueDependency, surveyId, surveyQuestion.id]);

    const handleUploadFiles = async (files: File[]) => {
        if (!files || files.length === 0) return;

        setIsUploading(true);
        setUploadError(null);
        const uploadedKeyPaths: string[] = [];
        const totalFiles = files.length;
        const fileNames = files.map(file => file.name);

        try {
            const uploadDests = await getRPC().surveys.getImageUploadUrls({
                surveyId: surveyId,
                questionId: surveyQuestion.id,
                count: totalFiles,
                uploadType: "SUBMISSION_ATTACHMENT",
                responseId: "",
                fileNames: fileNames,
            });

            const { destinations } = uploadDests;
            let failureCount = 0;
            const optimisticNewFiles: ImageUrlType[] = [];

            for (let i = 0; i < destinations.length; i++) {
                try {
                    await uploadImageThroughSignedUrl(
                        destinations[i].url,
                        files[i],
                    );
                    const keyPath = destinations[i].keyPath;
                    uploadedKeyPaths.push(keyPath);
                    const localUrl = URL.createObjectURL(files[i]);
                    optimisticNewFiles.push({ keyPath, url: localUrl });
                } catch (e) {
                    failureCount++;
                    console.error(`Failed to upload file ${files[i].name}:`, e);
                }
            }

            if (failureCount > 0) {
                setUploadError(
                    `${failureCount} file(s) failed to upload. Please try again.`,
                );
            }
            setDisplayFiles(currentDisplayFiles => [
                ...currentDisplayFiles,
                ...optimisticNewFiles,
            ]);
            if (uploadedKeyPaths.length > 0) {
                onChange([...value, ...uploadedKeyPaths]);
            }
            // biome-ignore lint/suspicious/noExplicitAny: Acceptable for error handling
        } catch (error: any) {
            console.error("Overall upload process error:", error);
            setUploadError(
                error.message || "An unexpected error occurred during upload.",
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveFile = (keyPathToRemove: string) => {
        const updatedValue = value.filter(
            keyPath => keyPath !== keyPathToRemove,
        );
        onChange(updatedValue);
    };

    if (!supportedUploadFormats || supportedUploadFormats.length === 0) {
        return (
            <Text color="gray.500">No supported upload formats specified.</Text>
        );
    }

    return (
        <VStack align="start" w="100%">
            {!isLoadingFiles && displayFiles.length > 0 && (
                <VStack align="start" w="100%" my={4}>
                    <Text fontWeight="medium" fontSize="md">
                        Associated Files
                    </Text>
                    <SimpleGrid columns={[1, 2, 3]} gap={4} w="100%">
                        {displayFiles.map(image => {
                            return (
                                <Box
                                    key={image.keyPath}
                                    position="relative"
                                    borderRadius="lg"
                                    overflow="hidden"
                                    boxShadow="md"
                                >
                                    <Link href={image.url} target="_blank">
                                        <Image
                                            width="100%"
                                            height="180px"
                                            objectFit="cover"
                                            src={image.url}
                                            alt={`Uploaded file ${image.keyPath.split("/").pop()}`}
                                        />
                                    </Link>

                                    <Button
                                        position="absolute"
                                        top={2}
                                        right={2}
                                        variant={"outline"}
                                        outlineColor={"#F9B4B4"}
                                        onClick={e => {
                                            e.preventDefault();
                                            handleRemoveFile(image.keyPath);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </Button>
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                </VStack>
            )}

            {(isLoadingFiles || isUploading) && (
                <Text color="gray.500" mt={2}>
                    {isLoadingFiles ? "Loading files..." : "Uploading files..."}
                </Text>
            )}

            {uploadError && (
                <Text color="red.500" mt={2}>
                    Error: {uploadError}
                </Text>
            )}

            <FileUpload.Root
                maxFiles={10}
                accept={getAcceptedMimeTypes()}
                onFileChange={({ acceptedFiles: files }) =>
                    handleUploadFiles(files)
                }
            >
                <FileUpload.HiddenInput />
                <FileUpload.Trigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        loading={isUploading}
                        loadingText="Uploading"
                    >
                        <UploadIcon />
                        Upload files
                    </Button>
                </FileUpload.Trigger>
            </FileUpload.Root>
        </VStack>
    );
};
